import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';

// A budget of 0 is the pause signal — mirrors retailers-dashboard, where the
// same PUT endpoint with `{ budget: 0 }` deactivates the plan (transitions
// live → historic). Quick-choose surfaces it explicitly so users discover the
// off option without having to drag the slider to its left extreme.
//
// The ladder is the same numbers in every market: a budget is denominated in
// the shop's own currency all the way through (`shop.currency` sets both the
// Google Ads account currency and the Stripe InvoiceItem), so these are ticks,
// not a converted GBP price.
const QUICK = [0, 3, 5, 10, 15, 20, 30, 50];
const MIN = 0;
const MAX = 50;

export function BudgetCard({ initialDaily = 5, onSave, mode = 'edit', currency }) {
	const money = (amount) => formatCurrency(amount, currency);
	const [saved, setSaved] = useState(initialDaily);
	const [value, setValue] = useState(initialDaily);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);

	// Sync down when the parent's read of the live plan changes (e.g. after
	// the SWR cache revalidates the new plan post-save).
	useEffect(() => {
		setSaved(initialDaily);
		setValue(initialDaily);
	}, [initialDaily]);

	// Daily budgets set outside the integer slider (HQ, legacy plans) can be
	// fractional, so ×30 picks up floating-point noise without the round.
	const monthly = Math.round((mode === 'activation' ? value : saved) * 30 * 100) / 100;
	const dirty = value !== saved;
	// In activation mode the user is committing their first plan, so the
	// Confirm button is always live (the default of 5 is a valid submission)
	// and there's no "Saved" state to fall back to. In edit mode the existing
	// Confirm-when-dirty + Saved-when-clean behaviour stays.
	const isActivation = mode === 'activation';
	// A budget of 0 means "pause" — only a meaningful action for an already-active plan.
	// During activation we hide it from quick-choose and lift the slider min
	// off zero so the user can't accidentally activate at zero and then wonder
	// why nothing's happening.
	const sliderMin = isActivation ? 3 : MIN;
	const quickValues = isActivation ? QUICK.filter((q) => q > 0) : QUICK;

	const pct = ((value - sliderMin) / (MAX - sliderMin)) * 100;

	const handleSave = async () => {
		if (!onSave) {
			setSaved(value);
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await onSave(value);
			setSaved(value);
		} catch (err) {
			setError(err?.response?.data?.message || err?.message || 'Could not save');
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="rounded-2xl border border-border/60 bg-gradient-soft p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-7">
			<div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_1.6fr] md:gap-10">
				<div className="md:border-r md:border-border/60 md:pr-8">
					<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
						{isActivation ? 'Forecast' : 'Current forecast'}
					</div>
					<h3 className="mt-1 text-lg font-bold text-foreground">Your Local Ads spend</h3>

					<div className="mt-5">
						<div className="text-4xl font-extrabold tracking-tight">{money(monthly)}</div>
						<div className="mt-1 text-sm text-muted-foreground">Total monthly spend</div>
					</div>
					<div className="mt-5">
						<div className="text-4xl font-extrabold tracking-tight">{money(isActivation ? value : saved)}</div>
						<div className="mt-1 text-sm text-muted-foreground">Total daily spend</div>
					</div>
					<p className="mt-5 text-xs text-muted-foreground">
						{isActivation
							? 'Based on the daily budget you choose. You can change or pause it any time after activation.'
							: 'Based on your current daily budget. You can change or pause it any time.'}
					</p>
				</div>

				<div>
					<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Daily budget</div>
					<h3 className="mt-1 text-lg font-bold text-foreground">Set how much to spend per day</h3>
					<p className="mt-1 text-sm text-muted-foreground">
						Slide to adjust. Stop or start any time — you only ever pay for the clicks you get.
					</p>

					<div className="relative mt-8">
						<div
							className="pointer-events-none absolute -translate-x-1/2 rounded-md bg-foreground/70 px-2 py-0.5 text-[11px] font-semibold text-background shadow-sm"
							style={{
								left: `calc(${pct}% + ${10 - pct * 0.2}px)`,
								bottom: 'calc(100% + 8px)'
							}}
						>
							{money(value)}
						</div>
						<Slider value={[value]} min={sliderMin} max={MAX} step={1} onValueChange={(v) => setValue(v[0])} />
						<div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
							<span>{money(sliderMin)}</span>
							<span>{money(MAX)}</span>
						</div>
					</div>

					<div className="mt-5">
						<div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick choose</div>
						<div className="flex flex-wrap gap-2">
							{quickValues.map((q) => (
								<button
									key={q}
									type="button"
									onClick={() => setValue(q)}
									className={cn(
										'rounded-full px-4 py-1.5 text-sm font-semibold transition',
										value === q ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:bg-muted/70'
									)}
								>
									{money(q)}
								</button>
							))}
						</div>
					</div>

					<div className="mt-6 flex items-center justify-between gap-4 border-t border-border/60 pt-5">
						<div className="text-sm">
							{isActivation ? null : (
								<>
									<span className="text-muted-foreground">Current daily budget: </span>
									<span className="font-bold text-foreground">{saved === 0 ? 'Paused' : `${money(saved)} per day`}</span>
								</>
							)}
							{error ? (
								<span className={isActivation ? 'text-destructive' : 'ml-2 text-destructive'}>
									{isActivation ? error : `· ${error}`}
								</span>
							) : null}
						</div>
						{isActivation || dirty ? (
							<div className="flex items-center gap-2">
								{!isActivation ? (
									<Button
										variant="ghost"
										onClick={() => setValue(saved)}
										disabled={saving}
										className="rounded-full text-muted-foreground hover:text-foreground"
									>
										Clear
									</Button>
								) : null}
								<Button
									onClick={handleSave}
									disabled={saving || (isActivation && value === 0)}
									variant={!isActivation && value === 0 ? 'destructive' : 'default'}
									className="rounded-full"
								>
									{saving
										? isActivation
											? 'Activating…'
											: 'Saving…'
										: isActivation
											? `Activate at ${money(value)}/day`
											: value === 0
												? 'Turn off ads'
												: `Confirm ${money(value)}/day`}
								</Button>
							</div>
						) : (
							<Button disabled className="rounded-full">
								{saved === 0 ? 'Paused' : 'Saved'}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
